import React from "react";
import DataTable from "react-data-table-component";
import { Button, Form, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  approvalKeluar,
  exportPengajuanKeluar,
  getPengajuanKeluar,
} from "stores";

function PengajuanKeluarKoperasi() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [detailRow, setDetailRow] = React.useState(null);
  const [status, setStatus] = React.useState("Disetujui");
  const [tanggalAwal, setTanggalAwal] = React.useState("");
  const [tanggalAkhir, setTanggalAkhir] = React.useState("");
  const [exporting, setExporting] = React.useState(false);
  const [keterangan, setKeterangan] = React.useState("");
  const [tglKeluar, setTglKeluar] = React.useState(
    new Date().toISOString().slice(0, 10),
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    const response = await getPengajuanKeluar(filter, searchQuery);
    if (response?.status === 200) setRows(response.data || []);
    else setRows([]);
    setLoading(false);
  }, [filter, searchQuery]);

  React.useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    load();
  }, [load]);

  function openApproval(row, nextStatus) {
    setSelected(row);
    setStatus(nextStatus);
    setKeterangan("");
    setTglKeluar(new Date().toISOString().slice(0, 10));
  }

  async function submitApproval(event) {
    event.preventDefault();
    if (status === "Ditolak" && !keterangan.trim()) {
      Swal.fire("Alasan wajib diisi", "Tuliskan alasan penolakan.", "warning");
      return;
    }
    const response = await approvalKeluar({
      idKeluar: selected.idKeluar,
      statusPengajuan: status,
      tglKeluar,
      keterangan,
    });
    if (response?.status === 200) {
      setSelected(null);
      Swal.fire("Berhasil", response.message, "success");
      load();
    } else {
      Swal.fire(
        "Gagal",
        response?.data?.message ||
          response?.message ||
          "Approval gagal diproses",
        "error",
      );
    }
  }

  async function handleExport() {
    if (!tanggalAwal || !tanggalAkhir) {
      Swal.fire(
        "Filter tanggal wajib diisi",
        "Pilih tanggal awal dan tanggal akhir pengajuan.",
        "warning",
      );
      return;
    }
    if (tanggalAwal > tanggalAkhir) {
      Swal.fire(
        "Range tanggal tidak valid",
        "Tanggal awal tidak boleh lebih besar dari tanggal akhir.",
        "warning",
      );
      return;
    }

    setExporting(true);
    const response = await exportPengajuanKeluar({
      tglAwal: tanggalAwal,
      tglAkhir: tanggalAkhir,
      statusPengajuan: filter,
      search: search.trim(),
    });
    if (response?.status === 200) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Pengajuan Keluar Koperasi.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExporting(false);
      return;
    }
    setExporting(false);
    Swal.fire(
      "Export gagal",
      response?.data?.message || "Data pengajuan keluar gagal diexport.",
      "error",
    );
  }

  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(
          new Date(value),
        )
      : "-";
  const formatMoney = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  const formatDateTime = (value) =>
    value
      ? new Intl.DateTimeFormat("id-ID", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(value))
      : "-";
  const tableCustomStyles = {
    headCells: {
      style: {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        wordBreak: "break-word",
        lineHeight: "1.2",
        minHeight: "52px",
      },
    },
  };
  const columns = [
    {
      name: "ID Pengajuan",
      selector: (row) => row.idKeluar,
      sortable: true,
      wrap: true,
    },
    {
      name: "User",
      selector: (row) => row.user?.nama || row.idUser,
      sortable: true,
      wrap: true,
    },
    {
      name: "Nopeg",
      selector: (row) => row.user?.nopeg || "-",
      sortable: true,
    },
    {
      name: "Tgl Pengajuan",
      selector: (row) => formatDate(row.datePengajuan),
      sortable: true,
    },
    {
      name: "Tgl Approval",
      selector: (row) => formatDate(row.tglApproval),
      sortable: true,
    },
    {
      name: "Tgl Keluar",
      selector: (row) => formatDate(row.tglKeluar),
      sortable: true,
    },
    { name: "Alasan", selector: (row) => row.alasan || "-", wrap: true },
    { name: "Status", selector: (row) => row.statusPengajuan, sortable: true },
    {
      name: "Nominal Dilunasi",
      selector: (row) => formatMoney(row.nominalPelunasan),
      right: true,
    },
    {
      name: "Aksi",
      minWidth: "285px",
      width: "285px",
      wrap: true,
      cell: (row) =>
        row.statusPengajuan === "Menunggu" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              width: "100%",
              padding: "4px 0",
            }}
          >
            <Button
              size="sm"
              variant="info"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setDetailRow(row)}
            >
              Detail
            </Button>
            <Button
              size="sm"
              variant="success"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => openApproval(row, "Disetujui")}
            >
              Setujui
            </Button>
            <Button
              size="sm"
              variant="danger"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => openApproval(row, "Ditolak")}
            >
              Tolak
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="info"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => setDetailRow(row)}
          >
            Detail
          </Button>
        ),
      ignoreRowClick: true,
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          margin: "20px 0 12px",
        }}
      >
        <h4 style={{ margin: 0 }}>Daftar Pengajuan Keluar Koperasi</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Form.Control
            type="search"
            placeholder="Cari nama / nopeg"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ maxWidth: 190 }}
          />
          <Form.Control
            as="select"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            style={{ maxWidth: 180 }}
          >
            <option value="">Semua status</option>
            <option value="Menunggu">Menunggu</option>
            <option value="Disetujui">Disetujui</option>
            <option value="Ditolak">Ditolak</option>
            <option value="Keluar">Keluar</option>
          </Form.Control>
          <Form.Control
            type="date"
            value={tanggalAwal}
            onChange={(event) => setTanggalAwal(event.target.value)}
            title="Tanggal awal pengajuan"
            style={{ maxWidth: 170 }}
          />
          <Form.Control
            type="date"
            value={tanggalAkhir}
            onChange={(event) => setTanggalAkhir(event.target.value)}
            title="Tanggal akhir pengajuan"
            style={{ maxWidth: 170 }}
          />
          <Button variant="success" onClick={handleExport} disabled={exporting}>
            {exporting ? "Menyiapkan..." : "Export Excel"}
          </Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        customStyles={tableCustomStyles}
        progressPending={loading}
        pagination
        responsive
      />
      <Modal
        show={Boolean(detailRow)}
        onHide={() => setDetailRow(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detail Pengajuan Keluar Koperasi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6>Data Pengajuan</h6>
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>ID Pengajuan:</strong> {detailRow?.idKeluar || "-"}
            </div>
            <div className="col-md-6">
              <strong>Status:</strong> {detailRow?.statusPengajuan || "-"}
            </div>
            <div className="col-md-6">
              <strong>User:</strong>{" "}
              {detailRow?.user?.nama || detailRow?.idUser || "-"}
            </div>
            <div className="col-md-6">
              <strong>Nopeg:</strong> {detailRow?.user?.nopeg || "-"}
            </div>
            <div className="col-md-6">
              <strong>Tanggal pengajuan:</strong>{" "}
              {formatDateTime(detailRow?.datePengajuan)}
            </div>
            <div className="col-md-6">
              <strong>Tanggal approval/keluar:</strong>{" "}
              {formatDateTime(detailRow?.tglKeluar || detailRow?.tglApproval)}
            </div>
            <div className="col-12">
              <strong>Alasan:</strong> {detailRow?.alasan || "-"}
            </div>
            <div className="col-12">
              <strong>Keterangan:</strong> {detailRow?.keterangan || "-"}
            </div>
          </div>

          <h6>Simpanan</h6>
          <div className="row mb-3">
            <div className="col-md-4">
              <strong>Total simpanan:</strong>
              <br />
              {formatMoney(detailRow?.detailKeuangan?.totalSimpanan)}
            </div>
            <div className="col-md-4">
              <strong>Simpanan terakhir:</strong>
              <br />
              {formatMoney(
                detailRow?.detailKeuangan?.simpananTerakhir?.nominal,
              )}
            </div>
            <div className="col-md-4">
              <strong>Tanggal simpanan terakhir:</strong>
              <br />
              {formatDate(
                detailRow?.detailKeuangan?.simpananTerakhir?.tglSimpanan ||
                  detailRow?.detailKeuangan?.tglSimpananAkhir,
              )}
            </div>
          </div>

          <h6>Pinjaman Masih Angsur</h6>
          {detailRow?.detailKeuangan?.pinjaman?.length ? (
            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>ID Pengajuan</th>
                    <th>Sisa Pokok</th>
                    <th>Sisa Admin</th>
                    <th>Total Sisa</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRow.detailKeuangan.pinjaman.map((item) => (
                    <tr key={item.idPengajuan}>
                      <td>{item.idPengajuan}</td>
                      <td>{formatMoney(item.nominalPokok)}</td>
                      <td>{formatMoney(item.nominalAdministrasi)}</td>
                      <td>{formatMoney(item.nominalTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">Tidak ada pinjaman yang masih angsur.</p>
          )}

          <h6>Riwayat Pengajuan</h6>
          {detailRow?.history?.length ? (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Status</th>
                    <th>Keterangan</th>
                    <th>ID User</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRow.history.map((item, index) => (
                    <tr key={`${item.date || "history"}-${index}`}>
                      <td>{formatDateTime(item.date)}</td>
                      <td>{item.status || "-"}</td>
                      <td>{item.keterangan || "-"}</td>
                      <td>{item.idUser || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">Belum ada history.</p>
          )}

          {!!detailRow?.pelunasan?.length && (
            <>
              <h6>Detail Pelunasan</h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>ID Pengajuan</th>
                      <th>Nominal Pokok</th>
                      <th>Nominal Admin</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRow.pelunasan.map((item, index) => (
                      <tr key={`${item.idPengajuan || "pelunasan"}-${index}`}>
                        <td>{item.idPengajuan || "-"}</td>
                        <td>{formatMoney(item.nominalPokok)}</td>
                        <td>{formatMoney(item.nominalAdministrasi)}</td>
                        <td>{formatMoney(item.nominalTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailRow(null)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} centered>
        <Form onSubmit={submitApproval}>
          <Modal.Header closeButton>
            <Modal.Title>
              {status === "Ditolak"
                ? "Tolak Pengajuan Keluar"
                : "Setujui Pengajuan Keluar"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <strong>{selected?.user?.nama || selected?.idUser}</strong>
            </p>
            <p className="text-muted">
              Sisa angsuran akan dilunasi otomatis jika pengajuan disetujui.
            </p>
            {status === "Disetujui" && (
              <Form.Group className="mb-3">
                <Form.Label>Tanggal keluar</Form.Label>
                <Form.Control
                  type="date"
                  value={tglKeluar}
                  onChange={(event) => setTglKeluar(event.target.value)}
                  required
                />
              </Form.Group>
            )}
            <Form.Group>
              <Form.Label>
                {status === "Ditolak" ? "Alasan penolakan" : "Keterangan"}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={keterangan}
                onChange={(event) => setKeterangan(event.target.value)}
                required={status === "Ditolak"}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              Batal
            </Button>
            <Button
              variant={status === "Ditolak" ? "danger" : "success"}
              type="submit"
            >
              {status === "Ditolak" ? "Tolak" : "Setujui"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default PengajuanKeluarKoperasi;
